import { useMemo, type ReactNode } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useWalletFrontendConfig } from "../hooks/payment/useWalletFrontendConfig";

interface Props {
  /** A checkout already has the platform publishable key in its facility config. */
  publishableKey?: string;
  children: ReactNode;
  unavailableFallback: ReactNode;
}

function PlatformElements({
  publishableKey,
  children,
}: {
  publishableKey: string;
  children: ReactNode;
}) {
  // Never include stripeAccount: saved-card SetupIntents belong to the platform.
  const stripe = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  return (
    <Elements key={publishableKey} stripe={stripe}>
      {children}
    </Elements>
  );
}

function StandalonePlatformElements({ children, unavailableFallback }: Props) {
  const config = useWalletFrontendConfig();
  if (!config.data?.publishable_key) return unavailableFallback;
  return (
    <PlatformElements publishableKey={config.data.publishable_key}>
      {children}
    </PlatformElements>
  );
}

/** A narrow account boundary around card setup, never around charge SCA. */
export function PlatformWalletElementsProvider(props: Props) {
  if (props.publishableKey) {
    return (
      <PlatformElements publishableKey={props.publishableKey}>
        {props.children}
      </PlatformElements>
    );
  }
  return <StandalonePlatformElements {...props} />;
}
